import { styled } from '@mui/material'
import {
    ContentChip,
    decimalToLocalizedString,
    MonetaryCell,
    MonetaryValue,
    MonetaryValueDataObjectType,
    PercentageCell,
    tokens,
} from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import type { SensitivityMatrixCopy } from './SensitivityMatrix'

type MatrixItemProps = {
    assetValue?: MonetaryValueDataObjectType
    deltaToBase?: { delta?: number; order?: number }
    assetValuePerSqm?: MonetaryValueDataObjectType
    multiplier?: number
    yieldRate?: number
    locale?: string
}

function mapDeltaOrderToColour(deltaOrder?: number) {
    switch (deltaOrder) {
        case 8:
            return 'grape'
        case 7:
            return 'red'
        case 6:
        case 5:
            return 'orange'
        case 4:
            return 'grey'
        case 3:
        case 2:
            return 'lime'
        case 1:
            return 'leaf'
        case 0:
            return 'green'
        case undefined:
        default:
            return 'grey'
    }
}

export default function SensitivityMatrixItem({
    assetValue,
    deltaToBase,
    assetValuePerSqm,
    multiplier,
    yieldRate,
    locale = 'de-DE',
}: MatrixItemProps) {
    const formattedAssetValue = (value?: MonetaryValueDataObjectType) => {
        const monetaryValue = !value || isNaN(value.amount) ? MonetaryValue.zero() : value
        return (
            <Typography color={tokens.ColorTextBoldest} variant="headline4">
                <MonetaryCell align="center" monetaryValue={monetaryValue} rounded locale={locale} />
            </Typography>
        )
    }
    const formattedDelta = (delta?: { delta?: number; order?: number }) => {
        if (delta === undefined) {
            return '-'
        }
        const color = mapDeltaOrderToColour(delta.order)
        return <ContentChip size={'small'} color={color} label={<PercentageCell percentage={delta.delta} locale={locale} />} />
    }
    const formattedValuePerSqm = (valuePerSqm?: MonetaryValueDataObjectType) => {
        if (valuePerSqm === undefined) {
            return '-'
        }
        return (
            <Typography variant="textS">
                <MonetaryCell monetaryValue={valuePerSqm} additionalUnit="/m²" rounded locale={locale} />
            </Typography>
        )
    }
    const formattedMultiplier = (mult?: number) => {
        if (mult === undefined) {
            return '-'
        }
        return <Typography variant="textS">{decimalToLocalizedString(mult, false, 2, undefined, locale)}</Typography>
    }
    const formattedYield = (yieldToBase?: number) => {
        if (yieldToBase === undefined) {
            return '-'
        }
        return (
            <Typography variant="textS">
                <PercentageCell align="center" percentage={yieldToBase} locale={locale} />
            </Typography>
        )
    }

    return (
        <MatrixItemContainer>
            <AssetValueContainer>{formattedAssetValue(assetValue)}</AssetValueContainer>
            <MatrixItemValues className="col1 row1">{formattedDelta(deltaToBase)}</MatrixItemValues>
            <MatrixItemValues>{formattedValuePerSqm(assetValuePerSqm)}</MatrixItemValues>
            <MatrixItemValues className="col1">{formattedMultiplier(multiplier)}</MatrixItemValues>
            <MatrixItemValues>{formattedYield(yieldRate)}</MatrixItemValues>
        </MatrixItemContainer>
    )
}

export function SensistivityMatrixLegendItem({ copy }: { copy: SensitivityMatrixCopy }) {
    return (
        <MatrixItemContainer>
            <AssetValueContainer>
                <Typography variant="textS">{copy.marketValue}</Typography>
            </AssetValueContainer>
            <MatrixItemValues className="col1 row1">
                <Typography variant="textS">{copy.deltaToMarketValue}</Typography>
            </MatrixItemValues>
            <MatrixItemValues>
                <Typography variant="textS">{copy.capitalValue}</Typography>
            </MatrixItemValues>
            <MatrixItemValues className="col1">
                <Typography variant="textS">{copy.actualMultiplier}</Typography>
            </MatrixItemValues>
            <MatrixItemValues>
                <Typography variant="textS">{copy.actualGrossInitialYield}</Typography>
            </MatrixItemValues>
        </MatrixItemContainer>
    )
}

const MatrixItemContainer = styled('div')`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto auto;

    & > div:nth-of-type(2),
    div:nth-of-type(4) {
        border-right: 1px solid ${tokens.ColorBorderDefault};
    }
    & > div:nth-of-type(2),
    div:nth-of-type(3) {
        border-bottom: 1px solid ${tokens.ColorBorderDefault};
    }
`

const AssetValueContainer = styled('div')`
    grid-column: span 2;
    text-align: center;
    padding: ${tokens.SizingSpacing075};
    gap: ${tokens.SizingSpacing050};
    border-bottom: 1px solid ${tokens.ColorBorderDefault};
`

const MatrixItemValues = styled('div')`
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;

    padding: ${tokens.SizingSpacing075} ${tokens.SizingSpacing100};
    gap: ${tokens.SizingSpacing050};
`
